// ---------------------------------------------------------------------------
// DataGroupPanel — Manage data group associations for biometric templates
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import {
  useDataGroups,
  useReferenceDataGroups,
  useAddDataGroup,
  useRemoveDataGroup,
} from '@/hooks/use-templates';

interface DataGroupPanelProps {
  templateId: string;
}

export function DataGroupPanel({ templateId }: DataGroupPanelProps) {
  const { toast } = useToast();
  const { data: currentGroups, isLoading: loadingCurrent } = useDataGroups(templateId);
  const { data: referenceGroups, isLoading: loadingRef } = useReferenceDataGroups();
  const addDataGroup = useAddDataGroup(templateId);
  const removeDataGroup = useRemoveDataGroup(templateId);

  const [selectedGroupId, setSelectedGroupId] = useState('');

  const currentIds = new Set(currentGroups?.map((g) => g.data_group_id) ?? []);
  const availableGroups = referenceGroups?.filter(
    (g) => g.is_active && !currentIds.has(g.data_group_id),
  ) ?? [];

  async function handleAdd() {
    if (!selectedGroupId) return;
    try {
      await addDataGroup.mutateAsync({ dataGroupId: selectedGroupId });
      setSelectedGroupId('');
      toast({ title: 'Data group added', variant: 'success' });
    } catch {
      toast({ title: 'Failed to add data group', variant: 'destructive' });
    }
  }

  async function handleRemove(dataGroupId: string) {
    try {
      await removeDataGroup.mutateAsync(dataGroupId);
      toast({ title: 'Data group removed', variant: 'success' });
    } catch {
      toast({ title: 'Failed to remove data group', variant: 'destructive' });
    }
  }

  if (loadingCurrent || loadingRef) {
    return <p className="text-sm text-carbon-400">Loading data groups...</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-carbon-600">
        Associate data groups with this biometric-triggered template.
        Readings from these groups will trigger the survey.
      </p>

      {/* Current associations */}
      {currentGroups && currentGroups.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {currentGroups.map((g) => (
            <Badge
              key={g.template_data_group_id}
              variant="default"
              className="gap-1.5 pr-1"
            >
              {/* PLM-654: some data groups have no short name; a link whose data
                  group is missing from the reference copy has no name, so show its id. */}
              {g.code && <>{g.code} &mdash; </>}
              {g.name ?? `Data group ${g.data_group_id}`}
              <button
                type="button"
                className="ml-1 rounded-full p-0.5 hover:bg-purple-200"
                onClick={() => handleRemove(g.data_group_id)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-sm text-carbon-400 italic">No data groups associated.</p>
      )}

      {/* Add new */}
      {availableGroups.length > 0 && (
        <div className="flex items-center gap-2">
          <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select a data group..." />
            </SelectTrigger>
            <SelectContent>
              {availableGroups.map((g) => (
                <SelectItem key={g.data_group_id} value={g.data_group_id}>
                  {g.code && <>{g.code} &mdash; </>}
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={!selectedGroupId || addDataGroup.isPending}
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      )}
    </div>
  );
}
